import "./FormFile.scss";
import { humanFileSize } from "@/core/util/helpers";
import ProgressLinear from "@/components/ui/progress/ProgressLinear";

export default {
  model: { prop: "inputValue", event: "change" },

  props: {
    inputValue: null,
    disabled: Boolean,
    readonly: Boolean,
    multiple: Boolean,
    loading: Boolean,
    label: String,
    accept: String,
    dropzone: String,
    color: {
      type: String,
      default: "blue-grad"
    }
  },

  data() {
    return {
      lazyValue: this.inputValue,

      dropzoneActive: false,
      dropedFiles: [],
      uploadProgress: {}
    };
  },

  watch: {
    inputValue(val) {
      this.lazyValue = val;
    },

    dropedFileList(after) {
      if (!after || !after.length) {
        setTimeout(() => {
          this.toggleDropzoneActive(false);
        }, 300);
      } else {
        setTimeout(() => {
          after.forEach((el, idx) => {
            this.$emit(
              "drop",
              el.file,
              {
                onUploadProgress: this.onUploadProgress(idx)
              },
              this.removePreview(idx)
            );
          });
        }, 300);
      }
    },

    dropzoneActive(after) {
      if (this.dropzoneAttachTarget) {
        let method = ["remove", "add"][+after];
        this.dropzoneAttachTarget.classList[method]("dropzone-inited");
      }
    }
  },

  computed: {
    internalValue: {
      get() {
        return this.lazyValue || [];
      },
      set(val) {
        this.lazyValue = val;
        this.$emit("change", val);
      }
    },

    files() {
      return Object.values(this.internalValue).filter(o => o instanceof File);
    },

    filesInfo() {
      return {
        count: this.files.length,
        size: humanFileSize(
          this.files.reduce((res, file) => res + file.size, 0)
        ),
        name: this.files.reduce(
          (res, file) => res + (res.length ? ", " : "") + file.name,
          ""
        )
      };
    },

    isDisabled() {
      return this.disabled || this.readonly || this.loading;
    },

    classes() {
      return {
        "form-file__label": true,
        files_disabled: this.isDisabled
      };
    },

    id() {
      return `form-file-input-${this._uid}`;
    },

    hasDropedFiles() {
      return this.dropedFiles && this.dropedFiles.length > 0;
    },

    dropedFileList() {
      const files = [...this.dropedFiles];
      return files;
    },

    dropzoneAttachTarget() {
      if (!this.dropzone || !this.dropzone.length) return null;
      let target = document.querySelector(this.dropzone);
      return target;
    }
  },

  methods: {
    onUploadProgress(idx) {
      return progressEvent => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        this.uploadProgress[idx] = percentCompleted;
      };
    },

    filePreview(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject();
      });
    },

    removePreview(idx) {
      return e => {
        e && e.stopPropagation();
        this.dropedFiles = this.dropedFiles.filter((o, i) => i !== idx);

        if (this.uploadProgress[idx]) delete this.uploadProgress[idx];
      };
    },

    genPreviewItem(src, idx) {
      return this.$createElement(
        "div",
        {
          staticClass: "form-file__dropzone__preview_item",
          attrs: {
            id: `preview-item-${idx}`
          }
        },
        [
          this.$createElement(
            "div",
            {
              staticClass: "preview_item__image"
            },
            [
              this.$createElement(
                "app-btn",
                {
                  props: {
                    iconOnly: true
                  },
                  on: {
                    click: this.removePreview(idx)
                  }
                },
                [
                  this.$createElement("svg-icon", {
                    props: { iconName: "close", size: 10 }
                  })
                ]
              ),
              this.$createElement("img", { attrs: { alt: "", src } })
            ]
          ),
          this.$createElement(ProgressLinear, {
            props: {
              value: this.uploadProgress[idx] || 0,
              rounded: true,
              height: 10,
              backgroundColor: "progress-bg",
              backgroundOpacity: 1
            }
          })
        ]
      );
    },

    genTransition(childs) {
      return this.$createElement(
        "transition",
        {
          props: { mode: "out-in", appear: true, name: "scale-transition" }
        },
        [childs]
      );
    },

    genFilesPreview() {
      let files = this.dropedFileList;

      return files.map((o, i) =>
        this.genTransition(this.genPreviewItem(o.preview, i))
      );
    },

    genDroparea() {
      const previewList = this.genFilesPreview();
      const wrapper = this.$createElement(
        "div",
        {
          staticClass: "form-file__dropzone_wrap"
        },
        [
          this.$createElement(
            "div",
            { staticClass: "form-file__dropzone_files" },
            [
              this.genTransition(
                this.$createElement(
                  "span",
                  {
                    directives: [
                      {
                        name: "show",
                        value: !previewList.length
                      }
                    ]
                  },
                  "Перетащите файл сюда"
                )
              ),
              !!previewList.length && previewList
            ]
          )
        ]
      );

      return this.$createElement(
        "div",
        {
          staticClass: "form-file__dropzone",
          class: {
            highlighted: this.dropzoneActive
          },
          on: {
            dragenter: this.onDragEnter,
            dragover: this.onDragOver,
            dragleave: this.onDragLeave,
            drop: this.onDrop
          },
          ref: "dropzone"
        },
        [wrapper]
      );
    },

    getLabelText(label) {
      if (this.filesInfo.count > 1)
        return `Файлов: ${this.filesInfo.count} (${this.filesInfo.size})`;
      else if (this.filesInfo.count)
        return `Файл: ${this.filesInfo.name} (${this.filesInfo.size})`;
      return label || "Прикрепить файл";
    },

    genDefaultSlot() {
      return [
        this.genInput("file", {
          ...this.$attrs
        }),
        this.dropzone && this.genDroparea(),
        this.genActivator()
      ];
    },

    genActivator() {
      const node = this.$createElement(
        "app-btn",
        {
          class: this.classes,
          attrs: {
            for: this.id
          },
          props: {
            tag: "label",
            round: true,
            ellipsis: true,
            color: this.color,
            loading: this.loading
          }
        },
        this.getLabelText(this.label)
      );

      this.activatorNode = node;

      return node;
    },

    genInput(type, attrs) {
      return this.$createElement("input", {
        attrs: Object.assign(
          {
            type,
            role: type,
            id: this.id,
            disabled: this.isDisabled,
            multiple: this.multiple,
            accept: this.accept
          },
          attrs
        ),
        on: {
          change: this.onChange
        },
        ref: "input"
      });
    },

    onChange(e) {
      if (this.isDisabled) return;
      const files = Object.values(e.target.files || []).filter(
        o => o instanceof File
      );

      this.internalValue = files;
    },

    // 'dragenter', 'dragover', 'dragleave', 'drop'
    toggleDropzoneActive(val) {
      this.dropzoneActive = val || !this.dropzoneActive;
    },

    handleDrop(e) {
      const dt = e.dataTransfer;
      const files = dt.files;

      this.handleFiles(files);
    },

    async handleFiles(files) {
      let list = !this.multiple && files.length > 1 ? [files[0]] : [...files];

      list = await Promise.all(
        list.map(async file => {
          const preview = await this.filePreview(file);
          return { file, preview };
        })
      );

      this.dropedFiles = list;
    },

    fullPrevent(e) {
      e.preventDefault();
      e.stopPropagation();
    },

    onDragEnter(e) {
      this.fullPrevent(e);
      this.toggleDropzoneActive(true);
    },
    onDragOver(e) {
      this.fullPrevent(e);
      this.toggleDropzoneActive(true);
    },
    onDragLeave(e) {
      this.fullPrevent(e);
      this.toggleDropzoneActive(false);
    },
    onDrop(e) {
      this.fullPrevent(e);
      this.handleDrop(e);
    },

    attachDropzone() {
      if (!this.dropzone || !this.dropzone.length) return;
      let target = document.querySelector(this.dropzone);

      if (target) {
        target.insertBefore(this.$refs.dropzone, target.firstChild);
      }
    }
  },

  beforeMount() {
    this.$nextTick(() => {
      if (this.activatorNode) {
        const activator = Array.isArray(this.activatorNode)
          ? this.activatorNode
          : [this.activatorNode];

        activator.forEach(node => {
          if (!node.elm) return;
          if (!this.$el.parentNode) return;

          const target =
            this.$el === this.$el.parentNode.firstChild
              ? this.$el
              : this.$el.nextSibling;

          this.$el.parentNode.insertBefore(node.elm, target);
        });
      }
    });
  },

  mounted() {
    this.attachDropzone();
  },

  render(h) {
    return h(
      "div",
      { staticClass: "form-file", attrs: {}, domProps: {} },
      this.genDefaultSlot()
    );
  }
};

/*

render(h, { props, data, slots }) {
  dedupeModelListeners(data)
  const children = rebuildSlots(slots(), h)

  if (props.mobile) {
    return h(VDataTableHeaderMobile, data, children)
  } else {
    return h(VDataTableHeaderDesktop, data, children)
  }
}


function rebuildFunctionalSlots(slots: { [key]: VNode[] | undefined }, h: CreateElement) {
  const children: VNode[] = []

  for (const slot in slots) {
    if (slots.hasOwnProperty(slot)) {
      children.push(h('template', { slot }, slots[slot]))
    }
  }

  return children
}


*/
